
import React, { useState, useEffect } from 'react';
import { BluetoothSearching, BluetoothOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Navbar } from '@/components/Navbar';
import BluetoothScanner from '@/components/BluetoothScanner';
import { isBluetoothSupported } from '@/utils/bluetoothService';
import { Button } from '@/components/ui/button';

const BluetoothDevices = () => {
  const [isBluetoothAvailable, setIsBluetoothAvailable] = useState(true);
  
  useEffect(() => {
    // Check if Bluetooth is supported
    const supported = isBluetoothSupported();
    setIsBluetoothAvailable(supported);
    
    if (!supported) {
      toast.warning('Bluetooth is not supported on this device or browser.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="pt-12 pb-6 px-6 bg-white shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">Bluetooth Devices</h1>
        <p className="text-gray-600 text-sm">Only verified client devices can receive payments</p>
      </header>
      
      <main className="container mx-auto px-6 py-8">
        {!isBluetoothAvailable ? (
          <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-6 w-6 text-yellow-500 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-yellow-800">Bluetooth Not Available</h3>
                <p className="text-gray-700 mt-1">
                  Web Bluetooth is not supported in this browser or device. Please try using Chrome, Edge, or Opera on desktop, or Chrome on Android.
                </p>
                <div className="mt-4">
                  <Button 
                    variant="outline"
                    className="bg-white hover:bg-gray-50"
                    onClick={() => window.location.href = "https://developer.mozilla.org/en-US/docs/Web/API/Web_Bluetooth_API#browser_compatibility"}
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Device Scanner</h2>
            <p className="text-gray-600 mb-6">
              Scan for transfer-enabled devices. Only verified clients can receive payments. 
              Non-verified devices will be redirected to email transfer.
            </p>
            <BluetoothScanner />
          </div>
        )}
        
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4">About Transfer-Enabled Devices</h2>
          <p className="text-gray-600">
            For security, Bluetooth Cash only works with verified client devices.
            Non-verified recipients will be redirected to email transfer.
            All transactions are protected with end-to-end encryption.
          </p>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-blue-700">Verified Clients</h3>
              <p className="text-sm text-blue-600 mt-1">
                Only verified client devices can receive Bluetooth payments directly
              </p>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg">
              <h3 className="font-medium text-purple-700">Security First</h3>
              <p className="text-sm text-purple-600 mt-1">
                All Bluetooth transactions are encrypted and verified before processing
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Navbar />
    </div>
  );
};

export default BluetoothDevices;
